import {
  forwardRef,
  ChangeEvent,
  ComponentPropsWithoutRef,
  ReactElement,
  MouseEvent,
  useId,
} from "react";
import cx from "classnames";
import { Icon } from "../Icon";
import CheckboxOutline from "@servicetitan/hammer-icon/mdi/round/check_box_outline_blank.svg";
import CheckboxChecked from "@servicetitan/hammer-icon/mdi/round/check_box.svg";
import CheckboxIndeterminate from "@servicetitan/hammer-icon/mdi/round/indeterminate_check_box.svg";

import styles from "./Checkbox.module.scss";
import { Svg, LayoutUtilProps } from "../../types";
import { CheckboxGroup } from "./CheckboxGroup";
import { useLayoutPropsUtil } from "../../hooks";
import { Helper, HelperProps } from "../../internal/components";

export type CheckboxState = {
  checked: boolean;
  value?: string | number | readonly string[];
};

/**
 * Props for the Checkbox component
 * @extends LayoutUtilProps
 * @extends Omit<ComponentPropsWithoutRef<"input">, "onChange" | "onClick" | "indeterminate">
 */
export type CheckboxProps = LayoutUtilProps &
  Omit<
    ComponentPropsWithoutRef<"input">,
    "onChange" | "onClick" | "indeterminate"
  > & {
    /**
     * Controlled state
     * @default false
     */
    checked?: boolean;

    /**
     * Uncontrolled state
     * @default false
     */
    defaultChecked?: boolean;

    /**
     * Indeterminate state of the checkbox
     * @accessibility Applies aria-checked="mixed"
     * @default false
     */
    indeterminate?: boolean;

    /**
     * Callback when checkbox is changed
     */
    onChange?: (
      e?: ChangeEvent<HTMLInputElement>,
      state?: CheckboxState,
    ) => void;

    /**
     * Callback when checkbox is clicked
     */
    onClick?: (e?: MouseEvent<HTMLInputElement>, state?: CheckboxState) => void;

    /**
     * label for checkbox
     * @accessibility This should either be a string or have text content inside for accessibility
     */
    label?: string | ReactElement;

    /**
     * Error state for the checkbox
     * @accessibility Applies aria-invalid="true"
     * @default false
     */
    error?: boolean;

    /**
     * Icon overrides
     */
    icons?: {
      checked?: Svg;
      indeterminate?: Svg;
      unchecked?: Svg;
    };

    description?: HelperProps["description"];

    /**
     * When `true`, hides the checkbox for visual users.
     * @default false
     */
    hideCheckbox?: boolean;
  };

const CheckboxElement = forwardRef<HTMLInputElement, CheckboxProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      indeterminate,
      onChange,
      onClick,
      value,
      className,
      disabled,
      error,
      icons,
      checked: checkedProp,
      defaultChecked,
      label,
      style,
      required,
      description,
      hideCheckbox = false,
      ...rest
    } = componentProps;
    const {
      checked: checkedIcon = CheckboxChecked,
      indeterminate: indeterminateIcon = CheckboxIndeterminate,
      unchecked: uncheckedIcon = CheckboxOutline,
    } = { ...icons };

    const isControlled = typeof checkedProp !== "undefined";

    const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e, { value, checked: checkedProp ?? e.target.checked });
    };

    const onClickHandler = (e: MouseEvent<HTMLInputElement>) => {
      onClick?.(e, {
        value,
        checked: checkedProp ?? (e.target as HTMLInputElement).checked,
      });
    };

    const helperUid = useId();

    const wrapperClassNames = cx(
      styles["wrapper"],
      { [styles["focus"]]: hideCheckbox },
      className,
    );

    const checkboxClassNames = cx(styles["checkbox"], {
      [styles["error"]]: error,
    });

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    return (
      <div
        className={wrapperClassNames}
        style={styleCombined}
        data-anv="checkbox"
      >
        <label className={checkboxClassNames}>
          <input
            type="checkbox"
            disabled={disabled}
            value={value}
            onChange={onChangeHandler}
            onClick={onClickHandler}
            aria-checked={
              indeterminate ? "mixed" : (checkedProp ?? defaultChecked)
            }
            checked={isControlled ? checkedProp : undefined}
            defaultChecked={defaultChecked}
            aria-invalid={error}
            aria-describedby={description ? helperUid : undefined}
            ref={ref}
            {...rest}
          />

          <span
            className={cx(styles["icon-wrapper"], {
              [styles["sr-only"]]: hideCheckbox,
            })}
            aria-hidden
          >
            <Icon
              size="large"
              svg={indeterminateIcon}
              className={styles["icon-indeterminate"]}
            />

            <Icon
              size="large"
              svg={checkedIcon}
              className={styles["icon-checked"]}
            />

            <Icon
              size="large"
              svg={uncheckedIcon}
              className={styles["icon-unchecked"]}
            />
          </span>

          <span className={cx({ [styles["focus-label"]]: hideCheckbox })}>
            {label}

            {required && (
              <span aria-hidden className={styles["required-asterisk"]}>
                *
              </span>
            )}
          </span>
        </label>

        {description ? (
          <div className={styles["helper-wrapper"]}>
            <Helper id={helperUid} description={description} />
          </div>
        ) : null}
      </div>
    );
  },
);

CheckboxElement.displayName = "Checkbox";

/**
 * Checkbox component for selecting one or more options from a list.
 *
 * Features:
 * - Controlled and uncontrolled state management
 * - Indeterminate state for partial selections
 * - Customizable icons for different states
 * - Error state with accessibility support
 * - Optional helper text and descriptions
 * - Hide checkbox option for visual-only interactions
 * - Full accessibility support with ARIA attributes
 * - Keyboard navigation support
 * - Layout utility props for positioning and spacing
 *
 * @example
 * <Checkbox
 *   label="Accept terms and conditions"
 *   onChange={(e, state) => console.log('Checked:', state.checked)}
 *   required
 * />
 */
export const Checkbox = Object.assign(CheckboxElement, {
  Group: CheckboxGroup,
});
